import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 미저장 닫기 보호(base `Modal`의 `isDirty`)를 **자식 폼의 state**까지 넓히는 한 쌍.
 *
 * base `Modal`은 `isDirty` 하나만 받는데, 상세 창 안에 «펼쳐지는 하위 폼»(확인 처리·결재선 카드 편집)이
 * 있으면 적던 내용은 그 자식의 state에 산다 — 부모는 그것을 모르므로 Esc·✕·바깥 클릭이 그대로 버린다.
 * 그래서 자식이 자기 dirty를 부모에게 «올리고», 부모가 그것을 `isDirty`에 OR로 더한다.
 *
 * - 자식: [[useReportDirty]] — 자기 dirty가 바뀔 때마다, 그리고 **언마운트될 때 false로** 알린다
 *   (폼을 닫거나 저장해 사라지면 그 자식 몫은 자동으로 빠진다).
 * - 부모: [[useDirtyChildren]] — 자식이 여럿(카드 N개·위반 행마다 폼)이면 key별로 모아 하나라도 dirty인지.
 *   자식이 하나뿐이면 `useState`의 setter를 `onDirtyChange`로 넘겨도 된다.
 *
 * 선례: `StoryDetailModal`(확인 처리·수동 판정 폼) · `SiteDetailModal` ← `SiteApprovalLineTab` ←
 * `ApprovalLineMetaCard`(결재선 편집 draft). 기준선(「열었을 때 값과 다른가」)은 자식이 스스로 정한다.
 */
export function useReportDirty(dirty: boolean, onDirtyChange: ((dirty: boolean) => void) | undefined) {
  // 부모가 매 렌더 새 함수를 넘겨도 effect가 다시 돌지 않게 최신 콜백을 ref로 든다.
  const callbackRef = useRef(onDirtyChange);
  callbackRef.current = onDirtyChange;

  useEffect(() => {
    callbackRef.current?.(dirty);
  }, [dirty]);

  useEffect(() => () => callbackRef.current?.(false), []);
}

export function useDirtyChildren() {
  const [dirtyByKey, setDirtyByKey] = useState<Record<string, boolean>>({});

  const report = useCallback((key: string, dirty: boolean) => {
    setDirtyByKey((prev) => (prev[key] === dirty ? prev : { ...prev, [key]: dirty }));
  }, []);

  const anyDirty = Object.values(dirtyByKey).some(Boolean);
  return { anyDirty, report };
}
